import type {
  BotLevelKnobs,
  PaceReport,
  PlaytestKind,
  PlaytestProgress,
  PlaytestWorkerOutbound,
  RacecraftReport,
} from '../../sim/bot/index.ts';
import type { MapDocument } from '../../sim/map/types.ts';
import type { TurnFeel } from '../../sim/turnFeel.ts';

export type PlaytestRun = {
  map: MapDocument;
  mapId: string;
  feel: TurnFeel;
  knobs: BotLevelKnobs;
  onProgress: (progress: PlaytestProgress) => void;
};

/**
 * Playtests for the bot workshop, in a module worker, so a run of a minute or more never blocks
 * the page: `run` races the bot solo (pace), `runPack` against Expert in both seat orders
 * (racecraft). One run at a time; `cancel` terminates the worker (its Expert caches go with it).
 * There is no main-thread fallback: a run that long would freeze the page.
 */
export type PlaytestClient = {
  run: (request: PlaytestRun) => Promise<PaceReport>;
  runPack: (request: PlaytestRun) => Promise<RacecraftReport>;
  cancel: () => void;
  destroy: () => void;
};

export function createPlaytestClient(): PlaytestClient {
  let worker: Worker | null = null;
  let nextId = 1;
  let active: { id: number; reject: (error: Error) => void } | null = null;

  const stop = (reason: string): void => {
    worker?.terminate();
    worker = null;
    active?.reject(new Error(reason));
    active = null;
  };

  function send<T>(kind: PlaytestKind, request: PlaytestRun): Promise<T> {
    if (active) {
      stop('replaced');
    }

    worker ??= new Worker(new URL('./playtestWorker.ts', import.meta.url), { type: 'module' });
    const id = nextId++;

    return new Promise<T>((resolve, reject) => {
      active = { id, reject };
      worker!.onmessage = (event: MessageEvent<PlaytestWorkerOutbound>) => {
        const message = event.data;

        if (message.id !== id) {
          return;
        }

        if (message.type === 'progress') {
          request.onProgress({ done: message.done, total: message.total, fraction: message.fraction });
          return;
        }

        active = null;

        if (message.type === 'error') {
          reject(new Error(message.message));
        } else {
          resolve(message.report as T);
        }
      };
      worker!.onerror = (event) => stop(event.message || 'playtest worker failed');
      worker!.postMessage({
        type: 'run',
        id,
        kind,
        map: request.map,
        mapId: request.mapId,
        feel: request.feel,
        knobs: request.knobs,
      });
    });
  }

  return {
    run: (request) => send<PaceReport>('solo', request),
    runPack: (request) => send<RacecraftReport>('pack', request),
    cancel: () => stop('cancelled'),
    destroy: () => stop('destroyed'),
  };
}
